import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { SubscriptionPlan } from "@/integrations/supabase/types/subscription";
import { Check, Crown, Loader2, Zap } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useSubscriptionHandler } from "./hooks/useSubscriptionHandler";
import { useToast } from "@/hooks/use-toast";
import { 
  getTokenAllocation, 
  getTokenTopUpDiscount, 
  getDocumentGeneration, 
  getAIAssistance, 
  getCaseCreation 
} from "./utils/planFeatures";

interface PlanCardProps {
  plan: SubscriptionPlan;
  loading: boolean;
  onSubscribe: (priceId: string) => Promise<void>;
  isCurrentPlan: boolean;
}

export function PlanCard({ plan, loading: externalLoading, isCurrentPlan }: PlanCardProps) {
  const { loading: subscriptionLoading, handleSubscribe } = useSubscriptionHandler();
  const { toast } = useToast();
  const loading = externalLoading || subscriptionLoading;
  
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: plan.currency || 'NOK',
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  const isPro = plan.name === 'Pro Bruker';

  const handleSubscription = async () => {
    if (!plan.price_id) {
      toast({
        title: "Feil",
        description: "Kunne ikke finne pris-ID for denne planen",
        variant: "destructive",
      });
      return;
    }

    try {
      await handleSubscribe(plan.price_id, plan.id);
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Feil ved abonnement",
        description: "Kunne ikke starte abonnementsprosessen. Vennligst prøv igjen.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className={`relative w-full ${isCurrentPlan ? "border-primary" : ""} ${isPro ? "shadow-lg" : ""}`}>
      {isPro && (
        <div className="absolute -top-3 left-0 right-0 flex justify-center">
          <span className="bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
            <Crown className="h-3 w-3" />
            Anbefalt
          </span>
        </div>
      )}
      <CardHeader className="p-4 md:p-6">
        <CardTitle className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
          <span className="text-lg md:text-xl">{plan.name}</span>
          {plan.price_amount > 0 && (
            <span className="text-base md:text-lg font-semibold">{formatPrice(plan.price_amount)}/mnd</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 p-4 md:p-6">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Tokens og Kreditter</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="flex-1">{getTokenAllocation(plan.name)} tokens per måned</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="flex-1">{getTokenTopUpDiscount(plan.name)} rabatt på token oppfylling</span>
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Dokumenter og AI</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="flex-1">Dokumentgenerering: {getDocumentGeneration(plan.name)}</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="flex-1">AI Chat Assistanse: {getAIAssistance(plan.name)}</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="flex-1">Saksopprettelse: {getCaseCreation(plan.name)}</span>
            </div>
            {isPro && (
              <>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span className="flex-1">Prioritert support</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <span className="flex-1">Rask prosessering</span>
                </div>
              </>
            )}
          </div>
        </div>

        {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Andre Funksjoner</h4>
              <div className="space-y-2 text-sm">
                {plan.features.map((feature: string, index: number) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="flex-1">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter className="p-4 md:p-6">
        <Button
          className="w-full transition-all duration-200 hover:scale-[1.02]"
          onClick={handleSubscription}
          disabled={loading || isCurrentPlan || !plan.price_id}
          variant={isPro ? "default" : "outline"}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Behandler...</span>
            </div>
          ) : isCurrentPlan ? (
            "Nåværende plan"
          ) : plan.name === "Gratis Bruker" ? (
            "Gratis plan"
          ) : (
            "Velg plan"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}